import {
  BudgetOverview,
  CompensationReview,
  CompensationReviewItem,
  PayBand,
  PayFrequency,
  ReviewResults,
  ReviewStatus,
} from './types';

// Factors that normalise a pay amount of a given frequency to a monthly figure.
// Mirrors the Salesforce Results panel: Monthly ×1, Annual ÷12,
// Weekly ×52/12, Bi-weekly ×26/12, Hourly ×173.33.
const MONTHLY_FACTOR: Record<PayFrequency, number> = {
  MONTHLY: 1,
  ANNUAL: 1 / 12,
  WEEKLY: 52 / 12,
  BIWEEKLY: 26 / 12,
  HOURLY: 173.33,
};

export const toMonthly = (amount: number, frequency: PayFrequency): number =>
  amount * MONTHLY_FACTOR[frequency];

const round2 = (value: number): number => Math.round(value * 100) / 100;

// Converts a row-currency amount into the review's corporate currency. The rate
// is 1 on single-currency orgs (or when the row is already in the corporate
// currency), so this is a no-op there. A `null` rate means the row currency has
// no known rate to corporate; we keep the amount as-is (best effort) — callers
// should suppress the conversion hint in that case so nothing is mislabelled.
export const toCorporate = (
  amount: number,
  rateToCorporate: number | null,
): number => round2(amount * (rateToCorporate ?? 1));

// New amount derived from a merit percentage applied to the reference amount.
export const applyMeritPercent = (
  referenceAmount: number,
  meritPercent: number,
): number => round2(referenceAmount * (1 + meritPercent / 100));

// The merit percentage implied by a reference → new amount change.
export const meritFromAmounts = (
  referenceAmount: number,
  newAmount: number,
): number => {
  if (referenceAmount === 0) {
    return 0;
  }
  return round2(((newAmount - referenceAmount) / referenceAmount) * 100);
};

// Columns the items grid can be sorted by. Pay Band (a visual bar) and the
// Include toggle are intentionally not sortable.
export type CompensationReviewSortColumn =
  | 'employee'
  | 'position'
  | 'frequency'
  | 'reference'
  | 'meritPercent'
  | 'newAmount';

export type SortDirection = 'asc' | 'desc';

export type CompensationReviewSort = {
  column: CompensationReviewSortColumn;
  direction: SortDirection;
};

// The default order: by employee name, ascending. Mirrors the order the
// backend returns items in (Name ASC) so the grid looks unchanged until the
// user clicks a column.
export const defaultItemsSort: CompensationReviewSort = {
  column: 'employee',
  direction: 'asc',
};

// The comparable value a row contributes for a given column. Strings are
// lower-cased so the sort is case-insensitive; amounts compare numerically.
const itemSortValue = (
  item: CompensationReviewItem,
  column: CompensationReviewSortColumn,
): string | number => {
  switch (column) {
    case 'employee':
      return item.employee.name.toLowerCase();
    case 'position':
      return item.position.toLowerCase();
    case 'frequency':
      return item.frequency;
    case 'reference':
      return item.referenceAmount;
    case 'meritPercent':
      return meritFromAmounts(item.referenceAmount, item.newAmount);
    case 'newAmount':
      return item.newAmount;
  }
};

const compareSortValues = (a: string | number, b: string | number): number =>
  typeof a === 'number' && typeof b === 'number'
    ? a - b
    : String(a).localeCompare(String(b));

// Sort the items by the chosen column/direction while keeping every employee's
// rows (an employee may have multiple salaries) grouped together.
//
// We first sort all rows by the active column, then collect them into
// per-employee buckets in first-seen order. Because a Map preserves insertion
// order, the employee whose top-sorting row comes first leads the list, that
// employee's remaining rows follow immediately (still in column order), and no
// other employee's row can wedge between them. The original index is the
// stable tie-breaker so equal values never reshuffle.
export const sortItemsGroupedByEmployee = (
  items: CompensationReviewItem[],
  sort: CompensationReviewSort,
): CompensationReviewItem[] => {
  const directionFactor = sort.direction === 'asc' ? 1 : -1;

  const originalIndex = new Map<CompensationReviewItem, number>();
  items.forEach((item, index) => originalIndex.set(item, index));

  const sorted = [...items].sort((a, b) => {
    const result = compareSortValues(
      itemSortValue(a, sort.column),
      itemSortValue(b, sort.column),
    );
    if (result !== 0) {
      return directionFactor * result;
    }
    return (originalIndex.get(a) ?? 0) - (originalIndex.get(b) ?? 0);
  });

  const buckets = new Map<string, CompensationReviewItem[]>();
  sorted.forEach((item) => {
    const bucket = buckets.get(item.employee.id);
    if (bucket) {
      bucket.push(item);
    } else {
      buckets.set(item.employee.id, [item]);
    }
  });

  return Array.from(buckets.values()).flat();
};

// Totals across the *included* rows only, converted to the corporate currency
// and normalised to monthly equivalents. Rows may be in different currencies,
// so each is converted to corporate before summing (mirrors the Salesforce
// results summary). Single-currency orgs convert by ×1, i.e. unchanged.
//
// Rows whose currency has no rate to corporate (`rateToCorporate === null`) are
// excluded: they cannot be expressed in the corporate currency, so counting
// them would silently mix currencies in a total labelled as corporate. (In
// practice every salary/org currency is active and has a rate, so this only
// guards a degenerate case.)
export const computeResults = (
  items: CompensationReviewItem[],
): ReviewResults => {
  const included = items.filter(
    (item) => item.included && item.rateToCorporate !== null,
  );

  const totalReferenceMonthly = included.reduce(
    (sum, item) =>
      sum +
      toMonthly(
        toCorporate(item.referenceAmount, item.rateToCorporate),
        item.frequency,
      ),
    0,
  );
  const totalNewMonthly = included.reduce(
    (sum, item) =>
      sum +
      toMonthly(
        toCorporate(item.newAmount, item.rateToCorporate),
        item.frequency,
      ),
    0,
  );

  const netChangeAbsolute = totalNewMonthly - totalReferenceMonthly;
  const netChangePercent =
    totalReferenceMonthly === 0
      ? 0
      : (netChangeAbsolute / totalReferenceMonthly) * 100;

  return {
    totalReferenceMonthly: round2(totalReferenceMonthly),
    totalNewMonthly: round2(totalNewMonthly),
    netChangeAbsolute: round2(netChangeAbsolute),
    netChangePercent: round2(netChangePercent),
  };
};

// The band comparison helpers only need the numeric bounds, not the full
// PayBand (currency/frequency flags are display-only).
type BandBounds = Pick<PayBand, 'min' | 'max'>;

// A proposed amount is out-of-band when it falls outside the linked pay band.
export const isOutOfBand = (
  newAmount: number,
  payBand: BandBounds | null,
): boolean => {
  if (!payBand) {
    return false;
  }
  return newAmount < payBand.min || newAmount > payBand.max;
};

// Where a value sits inside its band, as a 0–100 percentage (clamped). Used to
// position the marker on the pay-band bar.
export const bandPenetration = (
  amount: number,
  payBand: BandBounds,
): number => {
  // A fixed band (min === max) is a single point, so there is no range to
  // interpolate. Place the marker left/middle/right to signal below/at/above
  // that single value.
  if (payBand.max <= payBand.min) {
    if (amount < payBand.min) {
      return 0;
    }
    if (amount > payBand.min) {
      return 100;
    }
    return 50;
  }
  const ratio = (amount - payBand.min) / (payBand.max - payBand.min);
  return Math.min(100, Math.max(0, ratio * 100));
};

type StatusVariant = 'secondary' | 'info' | 'success' | 'danger' | 'warning';

// Maps a review status to a Bootstrap badge variant + translation key suffix.
export const statusMeta = (
  status: ReviewStatus,
): { variant: StatusVariant; labelKey: string } => {
  switch (status) {
    case 'PREPARING':
      return { variant: 'secondary', labelKey: 'preparing' };
    case 'SUBMITTED':
      return { variant: 'info', labelKey: 'submitted' };
    case 'APPROVED':
      return { variant: 'success', labelKey: 'approved' };
    case 'EXECUTED':
      return { variant: 'success', labelKey: 'executed' };
    case 'PROCESSED':
      return { variant: 'success', labelKey: 'processed' };
    case 'REJECTED':
      return { variant: 'danger', labelKey: 'rejected' };
    case 'FAILED':
      return { variant: 'danger', labelKey: 'failed' };
    case 'REVERTED':
      return { variant: 'warning', labelKey: 'reverted' };
    case 'UNKNOWN':
      return { variant: 'secondary', labelKey: 'unknown' };
  }
};

// A reviewer may edit/submit only while the review is still preparing, and only
// on a leaf: roll-ups carry no items and are approved via their children, so
// they have nothing to edit or submit (mirrored server-side in submitForApproval).
export const canEdit = (review: CompensationReview): boolean =>
  review.myRole === 'REVIEWER' &&
  review.status === 'PREPARING' &&
  review.isLeaf;

// A submitted review can be decided whenever there is an approval request
// routed to the running user (`approvalRequestId`). This is keyed off the
// request rather than `myRole` so that an assessing manager who is *also* the
// routed approver isn't locked out (myRole resolves to REVIEWER for them);
// a reviewer with no routed request has `approvalRequestId === null`, so they
// still don't get the action.
export const canApproveOrReject = (review: CompensationReview): boolean =>
  review.status === 'SUBMITTED' && review.approvalRequestId !== null;

// Reviews that need an action from the running user — drives the sidebar badge.
export const needsMyAction = (review: CompensationReview): boolean =>
  canEdit(review) || canApproveOrReject(review);

// Revert and audit logs stay in Salesforce; the Hub surfaces a pointer only.
export const isManagedInSalesforceOnly = (
  review: CompensationReview,
): boolean =>
  review.status === 'EXECUTED' ||
  review.status === 'PROCESSED' ||
  review.status === 'REVERTED' ||
  review.status === 'FAILED';

// ---------------------------------------------------------------------------
// Budget consumption helpers (FLA-8183)
// ---------------------------------------------------------------------------

// Width of a consumption bar, clamped to [0, 100] even when the true percent is
// over 100 (over budget) or negative (a net salary decrease). The label still
// shows the real percent; only the bar is clamped.
//
// A null percent means no percentage is computable: no budget → empty bar, but
// a zero-budget over-budget node (available 0, something consumed) still fills
// the bar so the red overspend state isn't hidden.
export const consumptionBarWidth = (
  consumedPercent: number | null,
  overBudget = false,
): number => {
  if (consumedPercent === null) {
    return overBudget ? 100 : 0;
  }
  return Math.min(100, Math.max(0, consumedPercent));
};

// A leaf's own budget consumption (monthly, corporate currency): the proposed
// NEW salary of every included item plus the CURRENT (reference) salary of every
// excluded item. Excluding an item only cancels its raise — the employee stays
// on payroll at their current salary, which still draws from the budget
// (FLA-8250) — so an excluded item reads as "no change" rather than freeing up
// budget. This is deliberately distinct from `computeResults`, which reports the
// raises actually given and stays included-only.
//
// Rows whose currency has no rate to corporate (`rateToCorporate === null`) are
// skipped, matching `computeResults`: they cannot be expressed in the corporate
// currency, so counting them would silently mix currencies.
export const computeBudgetConsumed = (
  items: CompensationReviewItem[],
): number =>
  round2(
    items
      .filter((item) => item.rateToCorporate !== null)
      .reduce(
        (sum, item) =>
          sum +
          toMonthly(
            toCorporate(
              item.included ? item.newAmount : item.referenceAmount,
              item.rateToCorporate,
            ),
            item.frequency,
          ),
        0,
      ),
  );

// Recomputes the overview from the reviewer's LOCAL edits against the
// server-provided `available`, so the donut and the pre-submit over-budget
// caution reflect edits immediately instead of the server snapshot. Roll-ups
// have no local items and keep the server-computed overview.
export const recomputeLeafOverview = (
  overview: BudgetOverview,
  items: CompensationReviewItem[],
): BudgetOverview => {
  const consumed = computeBudgetConsumed(items);
  const { available } = overview;
  const remaining = available === null ? null : round2(available - consumed);
  const consumedPercent =
    available === null || available === 0
      ? null
      : Math.round((consumed / available) * 1000) / 10;
  return {
    ...overview,
    consumed,
    remaining,
    consumedPercent,
    overBudget: available !== null && consumed > available,
  };
};

// A modified click (cmd/ctrl/shift/alt) or non-primary button should open the
// link's href in a new tab rather than triggering in-app navigation. Only a
// plain primary click is intercepted.
export const isModifiedClick = (event: {
  button?: number;
  metaKey?: boolean;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
}): boolean =>
  Boolean(
    (event.button ?? 0) !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey,
  );
